import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { OnyxEvent } from '../../models/event';
import { Rsvp } from '../../models/rsvp';
import { Observable } from 'rxjs/Observable';
import { Reservation, TransientReservation } from '../../models/reservation';
import { LogLevel, LogMessage, LogService } from '../../../shared/log/log.service';
import { TransientReservationStore } from '../../service/transient-reservation-store';
import { of } from 'rxjs/observable/of';
import { ConfirmationModalComponent } from '../../../shared/confirmation-modal/confirmation-modal.component';
import { FlashMessagesService } from '../../../shared/flash-messages/service/flash.messages.service';
import { Page } from '../../../shared/models/page';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalConfig } from '../../../shared/confirmation-modal/confirmation-modal-config';
import { EventValidationService } from 'app/event/service/event-validation.service';
import { TransientReservationLinkAction } from './TransientReservationLinkAction';


@Component({
  selector: 'pioneer-transient-table',
  styleUrls: ['./transient-table.component.css'],
  templateUrl: './transient-table.component.html'
})
export class TransientTableComponent implements OnInit {

  @Input()
  public event: OnyxEvent;

  @Input()
  public rsvp: Rsvp;

  @Input()
  public selectorType = 'radio';

  public transientReservationSelected: TransientReservation;

  @ViewChild('confirmLinkModal')
  public confirmLinkModal: ConfirmationModalComponent;

  confirmationModalConfig = new ConfirmationModalConfig();

  @Output()
  public updatePage: EventEmitter<{page: number, itemsPerPage: number}> = new EventEmitter<{page: number, itemsPerPage: number}>();

  noRecords = false;

  private _searchResults: Page<TransientReservation>;

  @Input()
  set searchResults(searchResults: Page<TransientReservation>) {
    this._searchResults = searchResults;
    if (this.searchResults && this.searchResults.content) {
      // searchResults.content is declared as a List<T> in the Page model but it is actually an array
      this.noRecords = this.searchResults.content.size === 0 || this.searchResults.content['length'] === 0;
      this.synchronizeSelectedReservations();
    }
    this.synchronizeAllReservationsChecked();
  }

  canChangeReservations$: Observable<boolean>;

  allReservationsChecked = false;

  confirmLinkAction: string;

  get searchResults(): Page<TransientReservation> {
    return this._searchResults;
  }

  get confirmLinkingMessage() {
    if (this.selectorType !== 'checkbox') {
      return 'EVENT.TRANSIENT_RESERVATION.CONFIRM_LINK_RESERVATION';
    } else {
      return this.translateService.instant(
        'EVENT.TRANSIENT_RESERVATION.CONFIRM_LINK_MULTIPLE_RESERVATIONS',
        { selectedReservations: this.transientReservationStore.selectedReservations.length }
      );
    }
  }

  constructor(private logService: LogService,
              private transientReservationStore: TransientReservationStore,
              private flashMessagesService: FlashMessagesService,
              private translateService: TranslateService,
              private eventValidationService: EventValidationService) { }

  ngOnInit() {
    this.canChangeReservations$ = this.eventValidationService.canChangeReservations();
  }

  obtainTransientReservationSelected(): Observable<TransientReservation> {
    let result$: Observable<TransientReservation> = new Observable<TransientReservation>();
    if (this.transientReservationSelected) {
      result$ = of(this.transientReservationSelected);
    } else {
      result$ = of(this.searchResults.content[0]);
    }
    return result$;
  }

  onSelect(selectedItem: any) {
    this.logService.log(new LogMessage(LogLevel.DEBUG, `Selected item Id: ${selectedItem.confirmation}`));
    if (this.transientReservationSelected) {
      this.transientReservationSelected.selected = false;
    }
    this.transientReservationSelected = selectedItem;
    this.transientReservationSelected.selected = true;
  }

  cleanSelected() {
    this.transientReservationSelected = undefined;
  }

  confirmLinkingAndPayViaGP() {
    this.confirmLinkAction = TransientReservationLinkAction.LINK_AND_PAY_VIA_GP.toString();
    this.confirmationModalConfig.confirm = 'EVENT.TRANSIENT_RESERVATION.LINK_SELECTED_AND_PAY_VIA_GROUPPAY_CONFIRMATION_BTN';
    this.confirmLinkModal.open();
  }

  confirmLinkingAndDoNotPayViaGP() {
    this.confirmLinkAction = TransientReservationLinkAction.LINK_AND_DO_NOT_PAY_VIA_GP.toString();
    this.confirmationModalConfig.confirm = 'EVENT.TRANSIENT_RESERVATION.LINK_SELECTED_AND_DO_NOT_PAY_VIA_GROUPPAY_CONFIRMATION_BTN';
    this.confirmLinkModal.open();
  }

  confirmLinkHandler(linkConfirmed: string | false) {
    if (linkConfirmed) {
      let transientReservationIdListToLink: number[];
      if (this.selectorType !== 'checkbox') {
        transientReservationIdListToLink = [this.transientReservationSelected.eventReservationId];
      } else {
        transientReservationIdListToLink = this.transientReservationStore.selectedReservations.map(r => r.eventReservationId);
      }
      const action = TransientReservationLinkAction.fromString(linkConfirmed);
      const doNotPay = action === TransientReservationLinkAction.LINK_AND_DO_NOT_PAY_VIA_GP;

      this.logService.log(new LogMessage(LogLevel.DEBUG,
        `Linking transient reservation IDs: ${transientReservationIdListToLink} - Not pay: ${doNotPay}`));

      this.linkReservation(transientReservationIdListToLink, doNotPay);
    }
    this.transientReservationSelected = null;
  }

  private linkReservation(transientReservationIdListToLink: number[], doNotPay: boolean): Observable<Reservation> {
    const page = this.searchResults.number;
    const itemsPerPage = this.searchResults.size;

    const $obs = this.transientReservationStore.linkTransientReservationsToEvent(transientReservationIdListToLink,
      this.event.eventProperty.eventPartyId, doNotPay);
    $obs.subscribe(() => {
      this.flashMessagesService.success('MESSAGES.SUCCESS.TRANSIENT_RESERVATIONS_LINKED');
      this.transientReservationStore.clearSelectedReservations();
      this.updatePage.emit({page, itemsPerPage});
      this.synchronizeSelectedReservations();
      this.synchronizeAllReservationsChecked();
    });
    return $obs;
  }

  onCheckboxChange(searchResult: TransientReservation) {
    searchResult.selected = !searchResult.selected;
    if (!searchResult.selected) {
      this.transientReservationStore.removeFromSelectedReservations(searchResult);
    } else {
      this.transientReservationStore.pushToSelectedReservations(searchResult);
    }
    this.synchronizeAllReservationsChecked();
  }

  onAllCheckboxChange(event) {
    this.searchResults.content.forEach(reservation => {
      reservation.selected = event.target.checked;
      if (event.target.checked) {
        this.transientReservationStore.pushToSelectedReservations(reservation);
      } else {
        this.transientReservationStore.removeFromSelectedReservations(reservation);
      }
    });
    this.allReservationsChecked = event.target.checked;
  }

  private synchronizeAllReservationsChecked() {
    if (this.searchResults && this.searchResults.content) {
      this.allReservationsChecked = this.searchResults.content.every(reservation => reservation.selected);
    } else {
      this.allReservationsChecked = false;
    }
  }

  hasSelectedReservations() {
    return this.transientReservationStore.selectedReservations.length > 0;
  }

  private synchronizeSelectedReservations() {
    this._searchResults.content.forEach(reservation => {
      reservation.selected = this.transientReservationStore.isReservationSelected(reservation);
    });
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/reservations/transient-table/transient-table.component.ts